import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import Process from '../components/Process';
import CallToAction from '../components/CallToAction';
import PillarIcon from '../components/PillarIcon';
import Section from '../components/ui/Section';
import Container from '../components/ui/Container';
import SectionHeading from '../components/ui/SectionHeading';
import usePageMeta from '../hooks/usePageMeta';
import pages from '../content/pages.json';
import { pillars } from '../data/services';

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: clamp(2rem, 5vw, 4.5rem);
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Prose = styled.div`
  p {
    color: var(--text-2);
    font-size: 1.0625rem;
    margin-bottom: 1.25rem;
    max-width: 62ch;
  }

  p:first-of-type {
    color: var(--text-1);
    font-size: clamp(1.125rem, 1.5vw, 1.25rem);
  }

  a {
    color: var(--text-1);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const Panel = styled.aside`
  padding: clamp(1.5rem, 3vw, 2.25rem);
  border-radius: var(--radius-lg);
  background: var(--bg-2);
  border: 1px solid var(--border-strong);

  h2 {
    font-family: var(--font-body);
    font-size: var(--fs-eyebrow);
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-3);
    margin-bottom: 1.25rem;
  }
`;

const PillarList = styled.ul`
  display: grid;
  gap: 0.25rem;

  li {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--border);
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.05rem;
  }

  li:last-child {
    border-bottom: none;
  }

  svg {
    color: var(--accent);
  }
`;

const Location = styled.p`
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border);
  color: var(--text-2);
  font-size: 0.95rem;
`;

const Values = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  li {
    padding: 1.75rem;
    border-radius: var(--radius-md);
    background: var(--surface);
    border: 1px solid var(--border);
  }

  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: var(--text-2);
    font-size: 0.95rem;
  }
`;

const values = [
  {
    title: 'End-to-end accountability',
    body: 'One team from wireframe to production. No “that’s not our scope” handoffs.',
  },
  {
    title: 'Security by default',
    body: 'Auth, audit logs, rate limiting, and least-privilege access are part of v1, not bolted on after a breach.',
  },
  {
    title: 'Honest scope',
    body: 'We tell you what we know, what we don’t, and what a realistic timeline looks like.',
  },
];

const About: React.FC = () => {
  usePageMeta(pages.about);

  return (
    <Layout>
      <PageHero
        eyebrow="About"
        title="A hands-on studio, from first call to production."
        lead="CuttingEdge DevOps designs and builds custom websites, mobile apps, cloud platforms, and cybersecurity work end-to-end. The same people you meet on the first call ship the code that goes to production."
      />

      <Section $tone="raised" aria-labelledby="who-title">
        <Container>
          <TwoColumn>
            <div>
              <SectionHeading id="who-title" eyebrow="Who we are" title="An independent software studio" />
              <Prose>
                <p>
                  We build personalized websites, web apps, mobile apps, REST APIs, cloud infrastructure, and
                  cybersecurity work for clients who don’t want to juggle five vendors to ship one product.
                </p>
                <p>
                  One team owns design, code, security, and operations, so the people answering your questions are
                  the people writing the code. And when a project launches, we stay on to maintain it.
                </p>
                <p>
                  We also write down what we learn along the way, from picking the one call to action a shop answers
                  to connecting AI tools safely, on our <Link to="/blog">blog</Link>.
                </p>
              </Prose>
            </div>
            <Panel>
              <h2>What we do</h2>
              <PillarList>
                {pillars.map((pillar) => (
                  <li key={pillar.id}>
                    <PillarIcon id={pillar.id} size={20} />
                    {pillar.title}
                  </li>
                ))}
              </PillarList>
              <Location>Based in Puerto Rico · Working remotely</Location>
            </Panel>
          </TwoColumn>
        </Container>
      </Section>

      <Section aria-labelledby="values-title">
        <Container>
          <SectionHeading id="values-title" eyebrow="Values" title="How we work" />
          <Values>
            {values.map((value) => (
              <li key={value.title}>
                <h3>{value.title}</h3>
                <p>{value.body}</p>
              </li>
            ))}
          </Values>
        </Container>
      </Section>

      <Process title="How we work with you" />

      <CallToAction />
    </Layout>
  );
};

export default About;
